// Termos de Uso do WindMaps.
// Renderizado em https://events.windmaps.app/terms (e no alias legado
// events.faromaps.app) e linkado pelas fichas de loja (Apple/Google exigem um
// link de Termos além da Privacidade) e pelo app.
//
// Nota de redação: o CDC (art. 51) considera NULA cláusula que isente
// totalmente o fornecedor de responsabilidade perante o consumidor. Por isso
// o documento enquadra o app como ferramenta AUXILIAR (não equipamento de
// segurança) e atribui o risco do esporte ao atleta, em vez de uma isenção
// total (que seria inválida). Ao alterar o texto, incremente TERMS_VERSION em
// `lib/terms.ts`.

import { TERMS_VERSION, TERMS_LAST_UPDATED } from '@/lib/terms';

const CONTACT_EMAIL = 'contato@windmaps.com.br';

export function TermsPage() {
  return (
    <article className="container mx-auto max-w-3xl py-12 prose prose-slate dark:prose-invert">
      <header className="mb-8">
        <h1 className="text-3xl font-semibold">Termos de Uso do WindMaps</h1>
        <p className="text-sm text-muted-foreground">
          Versão {TERMS_VERSION}, atualizada em {TERMS_LAST_UPDATED}
        </p>
      </header>

      <section className="space-y-4 text-sm leading-relaxed">
        <p>
          Estes Termos de Uso (&ldquo;Termos&rdquo;) regem o uso do{' '}
          <strong>WindMaps</strong>: o aplicativo móvel (Android e iOS) e o site{' '}
          <em>events.windmaps.app</em> (em conjunto, o &ldquo;Serviço&rdquo;). Ao
          criar uma conta ou utilizar o Serviço, você declara que leu, entendeu e
          concorda com estes Termos. Se você não concordar, não utilize o Serviço.
          O tratamento dos seus dados pessoais é regido pela nossa{' '}
          <a href="/privacy">Política de Privacidade</a>, que faz parte destes
          Termos.
        </p>

        <h2 className="text-xl font-semibold">1. Quem opera o Serviço</h2>
        <p>
          O WindMaps é operado por <strong>Felipe Landim</strong>. Para qualquer
          assunto relacionado a estes Termos, fale conosco pelo e-mail{' '}
          <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
        </p>

        <h2 className="text-xl font-semibold">2. O que o WindMaps é e o que não é</h2>
        <p>
          O WindMaps é uma <strong>ferramenta de registro e visualização</strong> de
          atividades esportivas: rastreia sessões de kitesurf, wing foil e kite
          foil por GPS, calcula estatísticas (distância, velocidade, tempo,
          saltos), exibe mapas e informações de apoio (vento, maré, pontos de
          atenção) e permite acompanhar a posição de membros de uma party de
          downwind ou de uma prova em tempo real.
        </p>
        <p>
          <strong>O WindMaps NÃO é:</strong>
        </p>
        <ul className="list-disc pl-6">
          <li>um equipamento de segurança ou de salvamento;</li>
          <li>
            um dispositivo de navegação náutica oficial nem substituto das cartas
            náuticas, sinalização e regras da autoridade marítima;
          </li>
          <li>
            um serviço de emergência, monitoramento de resgate ou de socorro;
          </li>
          <li>
            uma fonte oficial ou garantida de previsão de vento, maré, condições do
            mar ou de localização de perigos.
          </li>
        </ul>
        <p>
          O Serviço é um <strong>recurso auxiliar</strong>. Ele não substitui o seu
          treinamento, a sua experiência, o seu julgamento, o seu equipamento de
          segurança nem a sua avaliação direta das condições do local.
        </p>

        <h2 className="text-xl font-semibold">3. Risco da atividade e sua responsabilidade</h2>
        <p>
          Kitesurf, wing foil, kite foil e atividades aquáticas relacionadas são{' '}
          <strong>esportes de risco</strong>, que podem causar lesões graves ou
          morte. Ao utilizar o Serviço, você reconhece e aceita que pratica essas
          atividades <strong>por sua própria conta e risco</strong>. Você é o único
          responsável por:
        </p>
        <ul className="list-disc pl-6">
          <li>
            possuir treinamento, capacidade física e habilidade adequados à
            atividade que pratica;
          </li>
          <li>
            inspecionar e manter o seu próprio equipamento e utilizar os
            equipamentos de segurança apropriados;
          </li>
          <li>
            avaliar pessoalmente, antes e durante a atividade, as condições de
            vento, maré, mar, clima e do local, sem depender exclusivamente das
            informações exibidas no Serviço;
          </li>
          <li>
            conhecer e cumprir as leis, normas locais e determinações da autoridade
            marítima e dos órgãos competentes;
          </li>
          <li>
            decidir, com base no seu próprio julgamento, se e quando é seguro entrar
            ou permanecer na água.
          </li>
          <li>
            zelar pelo seu próprio dispositivo (celular, relógio ou outro aparelho)
            usado para o rastreamento, inclusive protegê-lo da exposição a água,
            areia, sol, impacto e ao consumo intenso de bateria inerente ao uso de
            GPS, decidindo, por sua conta e risco, levá-lo ou não para a água.
          </li>
        </ul>

        <h2 className="text-xl font-semibold">4. Sem garantia de precisão ou disponibilidade</h2>
        <p>
          O Serviço é fornecido <strong>&ldquo;no estado em que se encontra&rdquo;</strong>{' '}
          e <strong>&ldquo;conforme disponível&rdquo;</strong>. Os dados de GPS,
          velocidade, distância, alertas de proximidade de pontos de atenção, vento
          e maré podem conter imprecisões, atrasos ou falhas, inclusive porque
          dependem do hardware do seu dispositivo, da cobertura de sinal e de
          provedores externos de dados (por exemplo, serviços de mapa, vento e
          maré). Posições compartilhadas em tempo real podem atrasar, falhar ou não
          ser entregues. <strong>Nunca utilize o Serviço como fonte única para uma
          decisão de segurança.</strong>
        </p>
        <p>
          Não garantimos que o Serviço estará disponível de forma ininterrupta ou
          livre de erros, e podemos suspendê-lo ou alterá-lo para manutenção,
          melhoria ou por razões técnicas.
        </p>

        <h2 className="text-xl font-semibold">5. O Serviço não é socorro de emergência</h2>
        <p>
          O compartilhamento de posição em tempo real (em party de downwind, apoio
          ou provas) é um recurso de conveniência. Ele{' '}
          <strong>não garante</strong> que alguém esteja acompanhando a sua posição,
          nem que haja qualquer forma de resgate ou socorro disponível.{' '}
          <strong>Em caso de emergência, acione imediatamente os serviços de
          resgate e as autoridades competentes</strong>. Não dependa do Serviço.
        </p>

        <h2 className="text-xl font-semibold">6. Provas e eventos</h2>
        <p>
          Quando o Serviço é utilizado em provas ou eventos, o WindMaps fornece
          apenas o <strong>software de cadastro, rastreamento e visualização</strong>.
          A <strong>organização do evento</strong> é a única responsável pela
          segurança, regulamento, condução e fiscalização da prova, bem como pela
          decisão de realizá-la ou interrompê-la. O WindMaps não organiza, não
          arbitra e não responde pela segurança dos participantes de eventos de
          terceiros.
        </p>

        <h2 className="text-xl font-semibold">7. Conta e elegibilidade</h2>
        <p>
          Para usar os recursos que exigem login, você deve criar uma conta com
          informações verdadeiras e mantê-las atualizadas. Você é responsável por
          manter a confidencialidade das suas credenciais e por toda atividade
          realizada na sua conta. O Serviço não é destinado a menores de 13 anos;
          usuários entre 13 e 18 anos devem utilizá-lo com o consentimento e a
          supervisão de um responsável legal.
        </p>

        <h2 className="text-xl font-semibold">8. Uso aceitável</h2>
        <p>Ao utilizar o Serviço, você concorda em não:</p>
        <ul className="list-disc pl-6">
          <li>
            violar leis aplicáveis ou direitos de terceiros, nem usar o Serviço para
            fins ilícitos;
          </li>
          <li>
            acessar contas, dados ou sistemas sem autorização, ou tentar burlar
            medidas de segurança;
          </li>
          <li>
            interferir no funcionamento do Serviço, sobrecarregá-lo ou introduzir
            código malicioso;
          </li>
          <li>
            coletar ou divulgar dados de outros usuários sem a devida autorização;
          </li>
          <li>
            fazer engenharia reversa ou copiar o Serviço, salvo quando a lei
            expressamente permitir.
          </li>
        </ul>

        <h2 className="text-xl font-semibold">9. Conteúdo e propriedade intelectual</h2>
        <p>
          O Serviço, incluindo software, marca, identidade visual e demais
          elementos, pertence ao WindMaps e é protegido por lei. Estes Termos não
          transferem a você nenhum direito de propriedade, apenas uma licença
          pessoal, limitada, revogável e intransferível para usar o Serviço
          conforme estes Termos. Os dados das suas sessões continuam sendo seus;
          você nos concede a permissão necessária para hospedá-los, processá-los e
          exibi-los conforme descrito na <a href="/privacy">Política de
          Privacidade</a> e nos recursos que você utiliza.
        </p>

        <h2 className="text-xl font-semibold">
          10. Dados agregados e evolução da plataforma
        </h2>
        <p>
          O usuário permanece titular dos seus dados pessoais, que serão tratados
          na forma da <a href="/privacy">Política de Privacidade</a> e da
          legislação aplicável.
        </p>
        <p>
          O usuário concorda que a WindMaps poderá utilizar, de forma
          anonimizada, agregada e sem qualquer possibilidade de identificação
          individual, informações estatísticas decorrentes da utilização do
          Serviço, incluindo, exemplificativamente, trajetos, sessões esportivas,
          velocidade, distância, altitude, equipamentos utilizados, modalidades
          praticadas, condições ambientais, locais de prática, padrões de
          utilização e demais métricas geradas pela plataforma.
        </p>
        <p>
          Essas informações poderão ser utilizadas para aperfeiçoamento do
          Serviço, desenvolvimento de novos produtos e funcionalidades,
          treinamento de modelos de inteligência artificial, geração de
          estatísticas, pesquisas, estudos de mercado, relatórios, análises de
          desempenho e celebração de parcerias comerciais, sempre em conformidade
          com a Lei Geral de Proteção de Dados (Lei nº 13.709/2018) e demais
          normas aplicáveis.
        </p>
        <p>
          A empresa poderá consolidar informações estatísticas da comunidade para
          criação de rankings, mapas de calor, indicadores, relatórios,
          previsões, recomendações, funcionalidades inteligentes e demais
          produtos derivados, desde que tais informações não permitam a
          identificação individual dos usuários.
        </p>

        <h2 className="text-xl font-semibold">11. Limitação de responsabilidade</h2>
        <p>
          Na máxima extensão permitida pela legislação aplicável, o WindMaps e o seu
          operador não respondem por danos resultantes de: (i) decisões que você
          tomar com base nas informações exibidas pelo Serviço; (ii) imprecisão,
          atraso, indisponibilidade ou falha do Serviço ou de dados de provedores
          externos; (iii) uso indevido do Serviço ou descumprimento destes Termos;
          (iv) os riscos inerentes à prática esportiva, que são de sua
          responsabilidade; ou (v) perda, dano, mau funcionamento ou desgaste do
          dispositivo utilizado para o rastreamento, inclusive os decorrentes de
          exposição a água, areia, sol ou impacto e do consumo de bateria próprio do
          uso contínuo de GPS. O uso do aparelho durante a atividade é de sua
          responsabilidade.
        </p>
        <p>
          Nada nestes Termos exclui ou limita responsabilidades que não possam ser
          excluídas ou limitadas pela lei aplicável, em especial as garantias e
          direitos assegurados ao consumidor pelo Código de Defesa do Consumidor.
        </p>

        <h2 className="text-xl font-semibold">12. Suspensão e encerramento</h2>
        <p>
          Você pode parar de usar o Serviço e solicitar a exclusão da sua conta a
          qualquer momento, conforme a <a href="/privacy">Política de Privacidade</a>.
          Podemos suspender ou encerrar o seu acesso se você violar estes Termos ou
          se o uso representar risco à segurança ou ao funcionamento do Serviço.
        </p>

        <h2 className="text-xl font-semibold">13. Alterações destes Termos</h2>
        <p>
          Podemos atualizar estes Termos para refletir mudanças no Serviço ou na
          legislação. Quando o texto mudar de forma relevante, atualizamos a versão
          e a data no topo desta página. O uso continuado do Serviço após a
          atualização significa que você concorda com os novos Termos.
        </p>

        <h2 className="text-xl font-semibold">14. Lei aplicável e foro</h2>
        <p>
          Estes Termos são regidos pelas leis da República Federativa do Brasil.
          Fica eleito o foro do domicílio do consumidor para dirimir eventuais
          controvérsias, sem prejuízo de outro foro previsto em lei.
        </p>

        <h2 className="text-xl font-semibold">15. Contato</h2>
        <p>
          Dúvidas sobre estes Termos:{' '}
          <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
        </p>
      </section>
    </article>
  );
}
